import { css } from '@emotion/react';
import { semantic_colors, shapes, text_styles } from '../../foundation';
import { InputSize } from '../input/types';
import type { SelectChipSize, SelectChipVariant } from './SelectChip';
import { getAutoColorCode } from '../../styles';

const chip_base_css = css`
  display: flex;
  align-items: center;
  border: 1px solid ${semantic_colors.border.primary};
  background: ${semantic_colors.background.surface};
  ${shapes.border_radius.small};
  ${text_styles.Body_13_Medium};
  white-space: nowrap;
`;

export const input_chip_base_css = css`
  ${chip_base_css}

  &:disabled {
    color: ${semantic_colors.content.disabled};
    background-color: ${semantic_colors.background.disabled};
    border-color: ${semantic_colors.border.disabled};
  }
`;

const input_chip_x_base_css = css`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: 4px;
  color: ${semantic_colors.content.primary};
  transition: background-color 0.1s linear;
`;

export const INPUT_CHIP_DELETE_ICON_SIZE = 12;

export const input_chip_x_enabled_css = css`
  ${input_chip_x_base_css}
  cursor: pointer;
  &:hover {
    background: #f2f2f2;
  }
  &:active {
    background: #e6e6e6;
  }
`;

export const input_chip_x_disabled_css = css`
  cursor: not-allowed;
  ${input_chip_x_base_css}
  color: ${semantic_colors.content.disabled};
  background-color: ${semantic_colors.background.disabled};
`;

export const input_chip_height_size_css: Record<InputSize, string> = {
  xsmall: `
    height: 22px;
  `,
  small: `
    height: 24px;
  `,
  medium: `
    height: 26px;
  `,
  large: `
    height: 30px;
  `,
};

export const getDraggableInputChipSizeStyle = (size: InputSize, canDelete = true) => {
  const paddingRight = size === 'large' ? 8 : 4;
  const paddingLeft = canDelete ? paddingRight + 4 : paddingRight;

  switch (size) {
    case 'xsmall': {
      if (!canDelete) {
        return css`
          padding: 0;
          .grab-handle {
            padding: 0 ${paddingRight}px;
          }
        `;
      }
      return css`
        padding: 0 ${paddingRight}px 0 0;
        .grab-handle {
          padding: 0 0 0 ${paddingLeft}px;
        }
      `;
    }
    case 'small': {
      if (!canDelete) {
        return css`
          padding: 3px 0;
          .grab-handle {
            padding: 0 8px;
          }
        `;
      }
      return css`
        padding: 3px ${paddingRight}px 3px 0;
        .grab-handle {
          padding: 0 0 0 ${paddingLeft}px;
        }
      `;
    }
    case 'medium': {
      if (!canDelete) {
        return css`
          padding: 2px 0;
          .grab-handle {
            padding: 0 ${paddingRight}px;
          }
        `;
      }
      return css`
        padding: 2px ${paddingRight}px 2px 0;
        .grab-handle {
          padding: 0 0 0 ${paddingLeft}px;
        }
      `;
    }
    case 'large': {
      if (!canDelete) {
        return css`
          padding: 4px 0;
          .grab-handle {
            padding: 0 ${paddingRight}px;
          }
        `;
      }
      return css`
        padding: 4px ${paddingRight}px 4px 0;
        .grab-handle {
          padding: 0 0 0 ${paddingLeft}px;
        }
      `;
    }
    default:
      return '';
  }
};

export const getInputChipSizeStyle = (size: InputSize, canDelete?: boolean) => {
  const paddingRight = size === 'large' ? 8 : 4;
  const paddingLeft = canDelete ? paddingRight + 4 : paddingRight;

  switch (size) {
    case 'xsmall':
      return css`
        padding: 0px ${paddingRight}px 0px ${paddingLeft}px;
        gap: 2px;
        ${input_chip_height_size_css.xsmall}
      `;
    case 'small':
      return css`
        padding: 3px ${canDelete ? 4 : 8}px 3px 8px;
        gap: 2px;
        ${input_chip_height_size_css.small}
      `;
    case 'medium':
      return css`
        padding: 2px ${paddingRight}px 2px ${paddingLeft}px;
        gap: 2px;
        ${input_chip_height_size_css.medium}
      `;
    case 'large':
      return css`
        padding: 4px ${paddingRight}px 4px ${paddingLeft}px;
        gap: 4px;
        ${input_chip_height_size_css.large}
      `;
    default:
      return '';
  }
};

export const select_chip_base_css = css`
  display: flex;
  align-items: center;
  ${shapes.border_radius.small};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.1s linear;

  &:disabled {
    transition: none;
    opacity: 0.4;
  }
`;

export const select_chip_size_css: Record<SelectChipSize, string> = {
  xsmall: `
    height: 28px;
    padding: 3px 12px;
    gap: 4px;
    ${text_styles.Body_13_Medium}
  `,
  medium: `
    height: 38px;
    padding: 3px 14px;
    gap: 4px;
    ${text_styles.Body_14_Medium}
  `,
};

export const select_chip_icon_size_css: Record<SelectChipSize, number> = {
  xsmall: 12,
  medium: 14,
};

export const getSelectChipVariantStyle = (variant: SelectChipVariant, selected: boolean) => {
  if (variant === 'filled') {
    const background_color = selected ? semantic_colors.accent.primary : semantic_colors.fill.secondary;
    const color = selected ? semantic_colors.static.white : semantic_colors.content.primary;
    return css`
      border: none;
      color: ${color};
      background-color: ${background_color};

      &:hover:not([disabled]) {
        background-color: ${getAutoColorCode(background_color).hover};
      }
      &:active:not([disabled]) {
        background-color: ${getAutoColorCode(background_color).pressed};
      }
    `;
  }

  return css`
    border: 1px solid ${selected ? semantic_colors.accent.primary : semantic_colors.border.secondary};
    color: ${selected ? semantic_colors.accent.primary : semantic_colors.content.primary};
    background-color: ${semantic_colors.background.surface};

    &:hover:not([disabled]) {
      background-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
    }
    &:active:not([disabled]) {
      background-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
    }
  `;
};
